// ============================================================================
// MÓDULO 7 - UNIDAD 10: FAMILY & EARLY YEARS
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Temas: Family vocabulary + Childhood memories
// Vocabulario: Family tree, in-laws, blended families, baby items, life stages
// Gramática: "Did" for emphasis (I did call her!)
// Irregular verbs: fed, bit, sank, drew, hid, led
// INNOVACIÓN: Set 4 with interactive family story using passages!
// ============================================================================

export const M7_UNIT_10 = {
  id: "m7-u10",
  title: "Unit 10: Family & Early Years",
  topic: "Master family vocabulary, describe childhood memories, use 'did' for emphasis, and learn baby-related terms with UK/US differences!",
  
  grammarImage: "/images/grammar/m7-u10-did-emphasis-family.jpg",
  grammarText: `
    <h3>Family & Early Years</h3>
    
    <div class="family-vocabulary">
      <h4>Family Vocabulary</h4>
      
      <div class="basic-family">
        <h5>Basic Family (Core):</h5>
        <ul>
          <li><strong>Mother/Mom, Father/Dad</strong></li>
          <li><strong>Sister, Brother</strong> (siblings)</li>
          <li><strong>Daughter, Son</strong> (children)</li>
        </ul>
      </div>
      
      <div class="extended-family">
        <h5>Extended Family:</h5>
        <ul>
          <li><strong>Aunt</strong> (tía), <strong>Uncle</strong> (tío)</li>
          <li><strong>Cousin</strong> (primo/a) - same word for male/female!</li>
          <li><strong>Nephew</strong> (sobrino), <strong>Niece</strong> (sobrina)</li>
        </ul>
      </div>
      
      <div class="in-laws">
        <h5>In-Laws (Familia política):</h5>
        <ul>
          <li><strong>Mother-in-law, Father-in-law</strong></li>
          <li><strong>Sister-in-law, Brother-in-law</strong></li>
          <li><strong>Son-in-law, Daughter-in-law</strong></li>
        </ul>
      </div>
      
      <div class="blended">
        <h5>Blended Families (Familias recompuestas):</h5>
        <ul>
          <li><strong>Stepmother</strong> (madrastra), <strong>Stepfather</strong> (padrastro)</li>
          <li><strong>Stepsister, Stepbrother</strong> (hermanastros)</li>
          <li><strong>Half-sister, Half-brother</strong> (medios hermanos)</li>
        </ul>
      </div>
    </div>

    <div class="life-stages">
      <h4>Life Stages</h4>
      <ul>
        <li><strong>Baby</strong> (0-1 year) - bebé</li>
        <li><strong>Toddler</strong> (1-3 years) - niño pequeño</li>
        <li><strong>Child</strong> (3-12 years) - niño</li>
        <li><strong>Teenager</strong> (13-19 years) - adolescente</li>
        <li><strong>Adult</strong> (18+ years) - adulto</li>
        <li><strong>Elderly</strong> (65+ years) - anciano</li>
      </ul>
    </div>

    <div class="did-emphasis">
      <h4>"DID" for Emphasis (Insistir que algo REALMENTE pasó)</h4>
      
      <p><strong>Structure:</strong> Subject + DID + base verb</p>
      
      <div class="normal-vs-emphasis">
        <h5>Normal Past:</h5>
        <ul>
          <li>I called her. (simple statement)</li>
        </ul>
        
        <h5>With Emphasis:</h5>
        <ul>
          <li>I <strong>DID</strong> call her! (insisting - yes, it really happened!)</li>
        </ul>
      </div>
      
      <div class="use-cases">
        <h5>Use Cases:</h5>
        <ul>
          <li><strong>Defending yourself:</strong> "I did finish my homework!"</li>
          <li><strong>Contradicting:</strong> "I did study! I studied for 3 hours!"</li>
          <li><strong>Confirming:</strong> "She did apologize - I heard her!"</li>
        </ul>
        <p>NOTE: Stress "DID" when speaking!</p>
      </div>
    </div>

    <div class="baby-vocabulary">
      <h4>Baby Vocabulary - UK vs US</h4>
      <table>
        <tr>
          <th>US Term</th>
          <th>UK Term</th>
          <th>Spanish</th>
        </tr>
        <tr>
          <td>Pacifier</td>
          <td>Dummy</td>
          <td>Chupete</td>
        </tr>
        <tr>
          <td>Diaper</td>
          <td>Nappy</td>
          <td>Pañal</td>
        </tr>
        <tr>
          <td>Crib</td>
          <td>Cot</td>
          <td>Cuna</td>
        </tr>
        <tr>
          <td>Stroller</td>
          <td>Pram</td>
          <td>Cochecito</td>
        </tr>
      </table>
    </div>

    <div class="irregular-verbs">
      <h4>Irregular Past Verbs - Childhood Actions</h4>
      <ul>
        <li><strong>Feed → Fed</strong> (alimentar)</li>
        <li><strong>Bite → Bit</strong> (morder)</li>
        <li><strong>Sink → Sank</strong> (hundir)</li>
        <li><strong>Draw → Drew</strong> (dibujar)</li>
        <li><strong>Hide → Hid</strong> (esconder)</li>
        <li><strong>Lead → Led</strong> (liderar/guiar)</li>
      </ul>
    </div>
  `,

  iconType: "family",

  sets: [
    // ========================================================================
    // SET 1: BASIC FAMILY FOUNDATION 👨‍👩‍👧‍👦
    // ========================================================================
    {
      id: "m7-u10-s1",
      title: "Set 1: Basic Family Foundation 👨‍👩‍👧‍👦",
      context: `**Introducing Your Family**

New friend: "Tell me about your family! Who do you live with?"

You: "Let me introduce them..."`,
      
      questions: [
        {
          type: "match",
          q: "Match basic family terms with their Spanish translations:",
          pairs: [
            { left: "Mother/Mom", right: "Madre/Mamá" },
            { left: "Father/Dad", right: "Padre/Papá" },
            { left: "Sister", right: "Hermana" },
            { left: "Brother", right: "Hermano" }
          ],
          successtip: "✓ CORE FAMILY!\nThese = foundation vocabulary! Most basic relationships! 👨‍👩‍👧‍👦"
        },
        {
          type: "mc",
          q: "You: 'I have two ___.' (plural of sister/brother - general term)",
          options: [
            "siblings",
            "parents",
            "cousins"
          ],
          a: "siblings",
          successtip: "✓ SIBLINGS!\n'Siblings' = brothers and sisters together! Neutral term for both! 👧👦"
        },
        {
          type: "blank",
          q: "Friend: 'Do you have kids?' You: 'Yes, I have two ___.' (sons/daughters - general)",
          a: "children",
          accept: { ignoreCase: true, alts: ["kids"] },
          successtip: "✓ OFFSPRING!\n'Children' or 'kids' = sons and daughters! Plural of 'child'! 👶👧"
        },
        {
          type: "mc",
          q: "You: 'My ___ are very supportive.' (mother + father together)",
          options: [
            "parents",
            "siblings",
            "children"
          ],
          a: "parents",
          successtip: "✓ PARENTS!\n'Parents' = mother + father together! Collective term! 👨‍👩‍👧"
        },
        {
          type: "order",
          q: "Traduce: Tengo una hermana mayor y un hermano menor",
          a: "I have an older sister and a younger brother",
          successtip: "✓ AGE ORDER!\n'Older/younger' = specify age relationship! Elder sister, little brother! 👧👦"
        },
        {
          type: "mc",
          q: "You: 'My ___ is 5 years old.' (female child)",
          options: [
            "daughter",
            "son",
            "niece"
          ],
          a: "daughter",
          successtip: "✓ FEMALE CHILD!\n'Daughter' = your female child! 'Son' = male child! 👧"
        },
        {
          type: "blank",
          q: "You: 'I'm an only child - I don't have any ___.' (brothers/sisters)",
          a: "siblings",
          accept: { ignoreCase: true, alts: ["brothers or sisters", "brothers and sisters"] },
          successtip: "✓ ONLY CHILD!\n'Only child' = no siblings! 'Siblings' = brothers/sisters! 👤"
        },
        {
          type: "mc",
          q: "Friend: 'Is that your mom?' You: 'Yes, that's my ___.' (mother - formal)",
          options: [
            "mother",
            "sister",
            "aunt"
          ],
          a: "mother",
          successtip: "✓ FORMAL TERM!\n'Mother' = formal. 'Mom/Mum' = informal. Both correct! 👩"
        },
        {
          type: "order",
          q: "Traduce: Mis padres tienen tres hijos",
          a: "My parents have three children",
          successtip: "✓ FAMILY SIZE!\n'Three children' = can be any mix of sons/daughters! 👨‍👩‍👧‍👦"
        },
        {
          type: "mc",
          q: "Correct usage: 'My ___ is a doctor.' (one male sibling)",
          options: [
            "brother",
            "brothers",
            "sibling"
          ],
          a: "brother",
          successtip: "✓ SINGULAR MALE!\n'Brother' = one male sibling. 'Brothers' = plural. 'Sibling' = neutral! 👦"
        }
      ]
    },

    // ========================================================================
    // SET 2: EXTENDED FAMILY & IN-LAWS 👪
    // ========================================================================
    {
      id: "m7-u10-s2",
      title: "Set 2: Extended Family & In-Laws 👪",
      context: `**Family Reunion**

Cousin: "Welcome to the family reunion! Let me introduce everyone..."

You: "So many relatives! Help me remember who's who..."`,
      
      questions: [
        {
          type: "match",
          q: "Match extended family terms with their meanings:",
          pairs: [
            { left: "Aunt", right: "Your parent's sister (tía)" },
            { left: "Uncle", right: "Your parent's brother (tío)" },
            { left: "Cousin", right: "Your aunt/uncle's child (primo/a)" },
            { left: "Nephew", right: "Your sibling's son (sobrino)" }
          ],
          successtip: "✓ EXTENDED FAMILY!\nThese = your parents' siblings and their children! Broader family! 👪"
        },
        {
          type: "mc",
          q: "Cousin: 'My mom is your mom's sister, so she's your ___.'",
          options: [
            "aunt",
            "cousin",
            "sister"
          ],
          a: "aunt",
          successtip: "✓ PARENT'S SIBLING!\nYour parent's sister = your aunt! Your parent's brother = uncle! 👩"
        },
        {
          type: "blank",
          q: "You: 'Your dad and my dad are brothers, so you're my ___.' (same generation)",
          a: "cousin",
          accept: { ignoreCase: true },
          successtip: "✓ COUSIN RELATIONSHIP!\nYour aunt/uncle's children = your cousins! Same generation! Note: 'cousin' works for male/female! 👫"
        },
        {
          type: "mc",
          q: "Relative: 'This is your brother's son, so he's your ___.'",
          options: [
            "nephew",
            "cousin",
            "son"
          ],
          a: "nephew",
          successtip: "✓ SIBLING'S SON!\nYour sibling's son = nephew! Your sibling's daughter = niece! 👦"
        },
        {
          type: "order",
          q: "Traduce: Mi sobrina tiene 10 años (my sibling's daughter)",
          a: "My niece is 10 years old",
          successtip: "✓ NIECE!\nYour sibling's daughter = niece! Brother/sister's child! 👧"
        },
        {
          type: "mc",
          q: "Friend: 'Is that your husband's sister?' You: 'Yes, she's my ___.'",
          options: [
            "sister-in-law",
            "sister",
            "cousin"
          ],
          a: "sister-in-law",
          successtip: "✓ IN-LAW!\nYour spouse's sister = sister-in-law! Also: your sibling's wife = sister-in-law! 💍"
        },
        {
          type: "blank",
          q: "You: 'My wife's father is my ___-in-law.' (father)",
          a: "father",
          accept: { ignoreCase: true },
          successtip: "✓ SPOUSE'S PARENT!\nYour spouse's father = father-in-law! Your spouse's mother = mother-in-law! 👴💍"
        },
        {
          type: "mc",
          q: "Complete: 'My sister married John, so John is my ___.'",
          options: [
            "brother-in-law",
            "cousin",
            "uncle"
          ],
          a: "brother-in-law",
          successtip: "✓ SIBLING'S SPOUSE!\nYour sister's husband = brother-in-law! Your brother's wife = sister-in-law! 👨💍"
        },
        {
          type: "order",
          q: "Traduce: Mi cuñada es muy simpática (sibling's wife or spouse's sister)",
          a: "My sister-in-law is very nice",
          successtip: "✓ SISTER-IN-LAW!\n'Sister-in-law' works for both: spouse's sister OR sibling's wife! 👩💍"
        },
        {
          type: "mc",
          q: "Important: In English, 'cousin' is:",
          options: [
            "The same word for male and female (primo/prima)",
            "Different for male (cousin) and female (cousine)",
            "Only used for males"
          ],
          a: "The same word for male and female (primo/prima)",
          successtip: "✓ GENDER-NEUTRAL!\n'Cousin' = same for male/female! Unlike Spanish (primo/prima), English uses one word! 👫"
        }
      ]
    },

    // ========================================================================
    // SET 3: BLENDED FAMILIES 🏠
    // ========================================================================
    {
      id: "m7-u10-s3",
      title: "Set 3: Blended Families 🏠",
      context: `**Modern Family Structures**

Friend: "My parents divorced and remarried, so I have a big blended family now!"

You: "Tell me about them..."`,
      
      questions: [
        {
          type: "match",
          q: "Match blended family terms with their meanings:",
          pairs: [
            { left: "Stepmother", right: "Your father's new wife (madrastra)" },
            { left: "Stepfather", right: "Your mother's new husband (padrastro)" },
            { left: "Stepsister", right: "Your stepparent's daughter (hermanastra)" },
            { left: "Half-brother", right: "Same parent, different other parent (medio hermano)" }
          ],
          successtip: "✓ BLENDED FAMILIES!\nModern families often have step-relatives and half-siblings! 🏠"
        },
        {
          type: "mc",
          q: "Friend: 'My dad remarried, so his new wife is my ___.'",
          options: [
            "stepmother",
            "mother",
            "aunt"
          ],
          a: "stepmother",
          successtip: "✓ STEPMOTHER!\nYour father's new wife (not biological mom) = stepmother! 'Step-' = through marriage! 👩"
        },
        {
          type: "blank",
          q: "Friend: 'My mom married Tom, so Tom is my ___.' (mother's new husband)",
          a: "stepfather",
          accept: { ignoreCase: true },
          successtip: "✓ STEPFATHER!\nYour mother's new husband (not biological dad) = stepfather! 👨"
        },
        {
          type: "mc",
          q: "Friend: 'My stepfather has a daughter from his first marriage. She's my ___.'",
          options: [
            "stepsister",
            "half-sister",
            "cousin"
          ],
          a: "stepsister",
          successtip: "✓ STEPSISTER!\nYour stepparent's child from another relationship = stepsister/stepbrother! No shared parents! 👧"
        },
        {
          type: "order",
          q: "Traduce: Mi hermanastro vive con su madre (stepparent's son)",
          a: "My stepbrother lives with his mother",
          successtip: "✓ STEPBROTHER!\nStepbrother = stepparent's son! Different from half-brother! 👦"
        },
        {
          type: "mc",
          q: "Friend: 'My mom and stepdad had a baby together. That baby is my ___.'",
          options: [
            "half-brother / half-sister",
            "stepbrother / stepsister",
            "cousin"
          ],
          a: "half-brother / half-sister",
          successtip: "✓ HALF-SIBLING!\nYou share ONE parent (same mom OR same dad) = half-brother/half-sister! 👶"
        },
        {
          type: "blank",
          q: "Difference: 'Step-' = no shared parents. 'Half-' = ___ shared parent. (one/two)",
          a: "one",
          accept: { ignoreCase: true },
          successtip: "✓ KEY DIFFERENCE!\nStep = no blood relation. Half = one shared parent! Important distinction! 🎯"
        },
        {
          type: "mc",
          q: "Complete: 'My dad had another son with his new wife. That's my ___.'",
          options: [
            "half-brother (same father, different mother)",
            "stepbrother",
            "cousin"
          ],
          a: "half-brother (same father, different mother)",
          successtip: "✓ HALF-BROTHER!\nSame father + different mother = half-brother! One shared biological parent! 👦"
        },
        {
          type: "order",
          q: "Traduce: Mi madrastra es muy amable conmigo",
          a: "My stepmother is very kind to me",
          successtip: "✓ POSITIVE RELATIONSHIP!\nStepmothers can be wonderful! Not always like fairy tales! 👩💚"
        },
        {
          type: "mc",
          q: "Summary: In blended families, 'step-' means:",
          options: [
            "Related through parent's remarriage, no blood relation",
            "Blood relative",
            "Same as half-sibling"
          ],
          a: "Related through parent's remarriage, no blood relation",
          successtip: "✓ STEP- PREFIX!\n'Step-' = relationship through marriage, not blood! Modern families = complex but loving! 🏠💙"
        }
      ]
    },

    // ========================================================================
    // SET 4: THE GARCIA FAMILY REUNION - INTERACTIVE STORY 📖 (INNOVACIÓN!)
    // ========================================================================
    {
      id: "m7-u10-s4",
      title: "Set 4: The Garcia Family Reunion 📖",
      context: `**Interactive Family Story**

You're reading about the Garcia family reunion. 
Follow Maria's story and answer questions about her family and childhood memories!`,
      
      questions: [
        // ====================================================================
        // PASSAGE 1: Meeting the Family
        // ====================================================================
        {
          type: "passage",
          passage: `**Part 1: Meeting the Family**

Hi! I'm Maria Garcia. Welcome to our family reunion! Let me introduce everyone. This is my aunt Rosa and uncle Carlos - my dad's sister and her husband. Their daughter Sofia is my cousin. My sister-in-law Ana just arrived with her baby. My nephew Marco is adorable!`
        },
        {
          type: "mc",
          q: "How is Sofia related to Maria?",
          options: [
            "Cousin (Maria's aunt and uncle's daughter)",
            "Sister",
            "Niece"
          ],
          a: "Cousin (Maria's aunt and uncle's daughter)",
          successtip: "✓ COUSIN CONNECTION!\nYour aunt/uncle's children = your cousins! Same generation as you! 👫"
        },
        {
          type: "blank",
          q: "Ana is Maria's ___-in-law because Ana married Maria's brother.",
          a: "sister",
          accept: { ignoreCase: true },
          successtip: "✓ IN-LAW RELATIONSHIP!\nYour brother's wife = sister-in-law! Your spouse's sister also = sister-in-law! 💍"
        },
        {
          type: "mc",
          q: "What is Marco to Maria?",
          options: [
            "Nephew (her brother's son)",
            "Cousin",
            "Son"
          ],
          a: "Nephew (her brother's son)",
          successtip: "✓ NEPHEW!\nMaria's brother (Ana's husband) + Ana = Marco. Marco = Maria's nephew! 👶"
        },

        // ====================================================================
        // PASSAGE 2: Childhood Memories & "Did" Emphasis
        // ====================================================================
        {
          type: "passage",
          passage: `**Part 2: Childhood Memories**

At the reunion, we shared old stories. Uncle Carlos laughed, "Maria, I did teach you to ride a bike - remember?" Aunt Rosa added, "And you drew pictures on all the walls!" My cousin Sofia hid behind her mom, embarrassed. "We fed the dog under the table!" I admitted.`
        },
        {
          type: "mc",
          q: "Why did Uncle Carlos say 'I did teach you' instead of 'I taught you'?",
          options: [
            "For emphasis - to insist/confirm it really happened",
            "It's grammatically wrong",
            "They mean completely different things"
          ],
          a: "For emphasis - to insist/confirm it really happened",
          successtip: "✓ 'DID' FOR EMPHASIS!\n'Did + base verb' = emphasis! 'I DID teach you' = insisting it really happened! 💪"
        },
        {
          type: "blank",
          q: "Past tense of 'draw': 'You ___ pictures on the walls.' (irregular verb)",
          a: "drew",
          accept: { ignoreCase: true },
          successtip: "✓ IRREGULAR PAST!\nDraw → Drew → Drawn! Common childhood activity - drawing on walls! 🎨"
        },
        {
          type: "mc",
          q: "Past tense of 'hide': 'Sofia ___ behind her mom.'",
          options: [
            "hid",
            "hided",
            "hidden"
          ],
          a: "hid",
          successtip: "✓ IRREGULAR VERB!\nHide → Hid → Hidden! Kids often hide when embarrassed! 🙈"
        },
        {
          type: "blank",
          q: "Past tense of 'feed': 'We ___ the dog under the table.' (irregular)",
          a: "fed",
          accept: { ignoreCase: true },
          successtip: "✓ FED THE DOG!\nFeed → Fed → Fed! Classic kid behavior - feeding pets secretly! 🐕"
        },

        // ====================================================================
        // PASSAGE 3: The Baby & Baby Vocabulary
        // ====================================================================
        {
          type: "passage",
          passage: `**Part 3: The Baby of the Family**

Sister-in-law Ana unpacked Marco's bag. "He needs his diaper changed," she said, pulling out a fresh diaper. Marco reached for his pacifier and bottle. "Does he still sleep in a crib?" I asked. "Yes, and he's such a good toddler - learning to walk now!" Ana smiled proudly.`
        },
        {
          type: "mc",
          q: "What life stage is Marco at?",
          options: [
            "Toddler (1-3 years old, learning to walk)",
            "Teenager",
            "Baby (0-1 year)"
          ],
          a: "Toddler (1-3 years old, learning to walk)",
          successtip: "✓ TODDLER STAGE!\nToddler = 1-3 years old, learning to walk! Between baby and child! 👶🚶"
        },
        {
          type: "blank",
          q: "In British English, a 'diaper' is called a ___.",
          a: "nappy",
          accept: { ignoreCase: true },
          successtip: "✓ UK vs US!\nUS: diaper. UK: nappy. Same baby item - different words! 🍼🇬🇧🇺🇸"
        },
        {
          type: "mc",
          q: "In British English, a 'pacifier' is called a:",
          options: [
            "dummy",
            "soother",
            "binky"
          ],
          a: "dummy",
          successtip: "✓ UK TERM!\nUS: pacifier. UK: dummy. Same thing babies suck on! 👶"
        },
        {
          type: "blank",
          q: "In British English, a 'crib' is called a ___.",
          a: "cot",
          accept: { ignoreCase: true },
          successtip: "✓ BABY BED!\nUS: crib. UK: cot. Same baby bed! 🛏️🇬🇧🇺🇸"
        },

        // ====================================================================
        // PASSAGE 4: Growing Up Together
        // ====================================================================
        {
          type: "passage",
          passage: `**Part 4: Growing Up Together**

Sofia and I reminisced. "Remember when we were teenagers?" she asked. "We thought we were so grown up!" Now she's an adult with her own apartment. Uncle Carlos led us to the backyard. The old treehouse where we hid as children still stands. Time flies from toddler to adult!`
        },
        {
          type: "mc",
          q: "What life stage comes between 'child' and 'adult'?",
          options: [
            "Teenager (13-19 years old)",
            "Toddler",
            "Elderly"
          ],
          a: "Teenager (13-19 years old)",
          successtip: "✓ TEENAGE YEARS!\nTeenager = 13-19 years old! Between child and adult! 🧒➡️👨"
        },
        {
          type: "blank",
          q: "Past tense of 'lead': 'Uncle Carlos ___ us to the backyard.' (irregular)",
          a: "led",
          accept: { ignoreCase: true },
          successtip: "✓ LED THE WAY!\nLead → Led → Led! To guide or show the way! 👴➡️"
        },
        {
          type: "mc",
          q: "The story mentions life stages in order:",
          options: [
            "Toddler → Child → Teenager → Adult (correct progression!)",
            "Adult → Teenager → Child",
            "Baby → Adult → Teenager"
          ],
          a: "Toddler → Child → Teenager → Adult (correct progression!)",
          successtip: "🎉 STORY COMPLETE! 🎉\n\n✅ FAMILY RELATIONSHIPS LEARNED:\n• Aunt, uncle, cousin ✅\n• Sister-in-law, nephew ✅\n• Family reunion context! ✅\n\n✅ 'DID' FOR EMPHASIS:\n• 'I did teach you' = insisting! ✅\n• Used to confirm/defend ✅\n\n✅ IRREGULAR PAST VERBS:\n• Drew (draw) ✅\n• Hid (hide) ✅\n• Fed (feed) ✅\n• Led (lead) ✅\n\n✅ BABY VOCABULARY:\n• Diaper/Nappy ✅\n• Pacifier/Dummy ✅\n• Crib/Cot ✅\n\n✅ LIFE STAGES:\n• Toddler (1-3) ✅\n• Teenager (13-19) ✅\n• Adult (18+) ✅\n\n📖 INTERACTIVE STORY = COMPLETE!\nYou experienced family vocabulary in a real narrative! Amazing work! 👨‍👩‍👧‍👦✨"
        }
      ]
    },

    // ========================================================================
    // SET 5: LIFE STAGES 👶➡️👴
    // ========================================================================
    {
      id: "m7-u10-s5",
      title: "Set 5: Life Stages 👶➡️👴",
      context: `**Describing Ages**

Friend: "How old is your nephew?"

You: "He's at different life stages - let me explain..."`,
      
      questions: [
        {
          type: "match",
          q: "Match life stages with their age ranges:",
          pairs: [
            { left: "Baby", right: "0-1 year old" },
            { left: "Toddler", right: "1-3 years old" },
            { left: "Teenager", right: "13-19 years old" },
            { left: "Adult", right: "18+ years old" }
          ],
          successtip: "✓ LIFE PROGRESSION!\nEach stage = specific age range! From birth to adulthood! 👶➡️👨"
        },
        {
          type: "mc",
          q: "You: 'My nephew is 2 years old - he's a ___.'",
          options: [
            "toddler (1-3 years, learning to walk)",
            "baby",
            "teenager"
          ],
          a: "toddler (1-3 years, learning to walk)",
          successtip: "✓ TODDLER AGE!\nToddler = 1-3 years old! Learning to walk and talk! 👶🚶"
        },
        {
          type: "blank",
          q: "Friend: 'How old is your daughter?' You: 'She's 15 - a ___.' (13-19 years)",
          a: "teenager",
          accept: { ignoreCase: true, alts: ["teen"] },
          successtip: "✓ TEENAGE YEARS!\nTeenager/teen = 13-19 years old! Notice 'teen' in thirteen through nineteen! 🧒"
        },
        {
          type: "mc",
          q: "You: 'My son is only 8 months old - still a ___.'",
          options: [
            "baby (0-1 year)",
            "toddler",
            "child"
          ],
          a: "baby (0-1 year)",
          successtip: "✓ BABY STAGE!\nBaby = 0-1 year old! Before they learn to walk! 👶"
        },
        {
          type: "order",
          q: "Traduce: Mi hermana es adulta - tiene 25 años",
          a: "My sister is an adult - she is 25 years old",
          successtip: "✓ ADULTHOOD!\nAdult = 18+ years old! Legally adult at 18 in most countries! 👩"
        },
        {
          type: "mc",
          q: "You: 'My grandmother is 75 - she's ___.'",
          options: [
            "elderly (65+ years old)",
            "a teenager",
            "a toddler"
          ],
          a: "elderly (65+ years old)",
          successtip: "✓ SENIOR YEARS!\nElderly = 65+ years old! Also: 'senior citizen' or 'senior'! 👵"
        },
        {
          type: "blank",
          q: "Age range between toddler and teenager: '___' (3-12 years old)",
          a: "child",
          accept: { ignoreCase: true },
          successtip: "✓ CHILDHOOD!\nChild = 3-12 years old! Between toddler and teenager! School age! 👧"
        },
        {
          type: "mc",
          q: "Complete progression: Baby → Toddler → Child → ___ → Adult → Elderly",
          options: [
            "Teenager",
            "Infant",
            "Senior"
          ],
          a: "Teenager",
          successtip: "✓ COMPLETE LIFE STAGES!\nBaby → Toddler → Child → Teenager → Adult → Elderly! Full progression! 👶➡️👴"
        },
        {
          type: "order",
          q: "Traduce: Los niños pequeños aprenden a caminar (1-3 years old)",
          a: "Toddlers learn to walk",
          successtip: "✓ TODDLER MILESTONE!\nToddlers = learning to walk! That's what 'toddle' means - walk unsteadily! 🚶"
        },
        {
          type: "mc",
          q: "At what age do you become a teenager?",
          options: [
            "13 years old (when 'teen' appears in age)",
            "10 years old",
            "15 years old"
          ],
          a: "13 years old (when 'teen' appears in age)",
          successtip: "✓ TEEN BEGINS AT 13!\nThirTEEN = first age with 'teen'! Ends at nineTEEN! 13-19! 🧒"
        }
      ]
    },

    // ========================================================================
    // SET 6: BABY VOCABULARY - UK vs US 🍼
    // ========================================================================
    {
      id: "m7-u10-s6",
      title: "Set 6: Baby Vocabulary - UK vs US 🍼",
      context: `**Baby Shopping**

Shop assistant: "Are you looking for American or British baby products?"

You: "What's the difference?"`,
      
      questions: [
        {
          type: "match",
          q: "Match US terms with UK equivalents:",
          pairs: [
            { left: "Pacifier (US)", right: "Dummy (UK)" },
            { left: "Diaper (US)", right: "Nappy (UK)" },
            { left: "Crib (US)", right: "Cot (UK)" },
            { left: "Stroller (US)", right: "Pram (UK)" }
          ],
          successtip: "✓ US vs UK BABY ITEMS!\nSame items, different words! Important for parents! 👶🇺🇸🇬🇧"
        },
        {
          type: "mc",
          q: "US parent: 'The baby needs a ___.' UK parent: 'The baby needs a dummy.'",
          options: [
            "pacifier (chupete)",
            "diaper",
            "crib"
          ],
          a: "pacifier (chupete)",
          successtip: "✓ PACIFIER/DUMMY!\nUS: pacifier. UK: dummy. Thing babies suck on to calm down! 👶"
        },
        {
          type: "blank",
          q: "UK: 'nappy' = US: '___' (pañal)",
          a: "diaper",
          accept: { ignoreCase: true },
          successtip: "✓ DIAPER/NAPPY!\nUS: diaper. UK: nappy. Essential baby item! Changed many times daily! 👶💩"
        },
        {
          type: "mc",
          q: "US: 'crib' = UK: '___' (cuna)",
          options: [
            "cot",
            "pram",
            "dummy"
          ],
          a: "cot",
          successtip: "✓ BABY BED!\nUS: crib. UK: cot. Where babies sleep! 🛏️👶"
        },
        {
          type: "order",
          q: "Traduce usando término US: El bebé está en su cochecito (baby transport)",
          a: "The baby is in his stroller",
          successtip: "✓ US TERM!\nUS: stroller. UK: pram. Wheeled baby transport! 🚼"
        },
        {
          type: "mc",
          q: "British parent: 'Put the baby in the pram.' American equivalent:",
          options: [
            "Put the baby in the stroller",
            "Put the baby in the crib",
            "Put the baby in the car"
          ],
          a: "Put the baby in the stroller",
          successtip: "✓ PRAM = STROLLER!\nBoth = wheeled baby carriage! For walking with baby! 🚼🚶"
        },
        {
          type: "blank",
          q: "Universal baby items (same in US/UK): baby ___, high chair, car seat",
          a: "bottle",
          accept: { ignoreCase: true },
          successtip: "✓ UNIVERSAL TERMS!\nSome baby items have same name in US and UK! Bottle, high chair, car seat! 🍼"
        },
        {
          type: "mc",
          q: "What do babies drink from?",
          options: [
            "Baby bottle (same US/UK)",
            "Pacifier/dummy",
            "Crib/cot"
          ],
          a: "Baby bottle (same US/UK)",
          successtip: "✓ BABY BOTTLE!\n'Bottle' = same term in US and UK! For milk/formula! 🍼"
        },
        {
          type: "order",
          q: "Traduce usando UK: Cambia el pañal del bebé",
          a: "Change the baby's nappy",
          successtip: "✓ UK TERM!\n'Change the nappy' (UK) vs 'change the diaper' (US)! Same action! 👶"
        },
        {
          type: "mc",
          q: "Summary: Why do US and UK have different baby vocabulary?",
          options: [
            "Different English dialects developed different terms",
            "They use completely different items",
            "One is wrong"
          ],
          a: "Different English dialects developed different terms",
          successtip: "✓ DIALECT DIFFERENCES!\nSame items, different names! Neither is wrong - just regional! 🇺🇸🇬🇧"
        }
      ]
    },

    // ========================================================================
    // SET 7: "DID" FOR EMPHASIS - INTRODUCTION 💪
    // ========================================================================
    {
      id: "m7-u10-s7",
      title: "Set 7: 'Did' for Emphasis - Introduction 💪",
      context: `**Defending Yourself**

Mom: "You didn't clean your room!"

You: "I DID clean it! Look!"`,
      
      questions: [
        {
          type: "mc",
          q: "What's the difference? 'I cleaned it' vs 'I DID clean it'",
          options: [
            "'Did clean' adds emphasis - insisting it really happened",
            "They mean opposite things",
            "'Did clean' is future tense"
          ],
          a: "'Did clean' adds emphasis - insisting it really happened",
          successtip: "✓ EMPHASIS!\n'I DID clean it' = emphatic! Insisting/confirming it really happened! Stronger than regular past! 💪"
        },
        {
          type: "blank",
          q: "Structure: Subject + ___ + base verb (for emphasis in past)",
          a: "did",
          accept: { ignoreCase: true },
          successtip: "✓ DID + BASE VERB!\n'Subject + DID + base verb' = emphatic past! 'I did call', 'She did finish'! 💪"
        },
        {
          type: "mc",
          q: "Friend: 'You didn't call me!' You: '___!' (emphatic contradiction)",
          options: [
            "I did call you",
            "I called you",
            "I will call you"
          ],
          a: "I did call you",
          successtip: "✓ CONTRADICTING!\n'I did call you!' = emphatic defense! Stronger than simple 'I called you'! 📞💪"
        },
        {
          type: "order",
          q: "Add emphasis: 'She finished her homework' → 'She ___ ___ her homework'",
          a: "did finish",
          successtip: "✓ ADDING EMPHASIS!\n'Did finish' = emphasizing she really completed it! Defending or confirming! ✅💪"
        },
        {
          type: "mc",
          q: "When do we use 'did' for emphasis?",
          options: [
            "To insist, defend, or confirm something really happened",
            "For all past tense sentences",
            "Only in questions"
          ],
          a: "To insist, defend, or confirm something really happened",
          successtip: "✓ USE CASES!\nUse when: defending yourself, contradicting, or strongly confirming! Not always! 🎯"
        },
        {
          type: "blank",
          q: "Teacher: 'You didn't study!' Student: 'I ___ study! For 3 hours!' (emphasis)",
          a: "did",
          accept: { ignoreCase: true },
          successtip: "✓ STUDENT DEFENSE!\n'I did study!' = emphatic defense! Insisting they really studied! 📚💪"
        },
        {
          type: "mc",
          q: "Complete with emphasis: 'He ___ apologize - I heard him!'",
          options: [
            "did",
            "do",
            "does"
          ],
          a: "did",
          successtip: "✓ CONFIRMING!\n'He did apologize' = confirming it happened! You're certain! 🗣️✅"
        },
        {
          type: "order",
          q: "Traduce con énfasis: ¡SÍ te llamé ayer!",
          a: "I did call you yesterday",
          successtip: "✓ EMPHATIC SPANISH!\n'Sí te llamé' = 'I DID call you'! Spanish 'sí' + verb = English 'did' emphasis! 📞"
        },
        {
          type: "mc",
          q: "Error check: 'I did cleaned my room' - what's wrong?",
          options: [
            "Should be 'did clean' (base verb, not past)",
            "Nothing wrong",
            "Should be 'did cleaning'"
          ],
          a: "Should be 'did clean' (base verb, not past)",
          successtip: "✓ COMMON ERROR!\n'Did' + BASE verb! ✅ 'did clean' ❌ 'did cleaned'! Don't use past form with 'did'! ⚠️"
        },
        {
          type: "mc",
          q: "When speaking, which word do you stress? 'I DID call you!'",
          options: [
            "DID (stress the emphasis word!)",
            "call",
            "you"
          ],
          a: "DID (stress the emphasis word!)",
          successtip: "✓ PRONUNCIATION!\nStress 'DID' when speaking! 'I DID call you!' = makes it emphatic! 🗣️💪"
        }
      ]
    },

    // ========================================================================
    // SET 8: "DID" FOR EMPHASIS - PRACTICE ✅
    // ========================================================================
    {
      id: "m7-u10-s8",
      title: "Set 8: 'Did' for Emphasis - Practice ✅",
      context: `**Emphasis in Different Situations**

Practice using 'did' to emphasize in various contexts...`,
      
      questions: [
        {
          type: "mc",
          q: "Boss: 'You didn't submit the report.' You: '___!' (emphatic defense)",
          options: [
            "I did submit it",
            "I submitted it",
            "I will submit it"
          ],
          a: "I did submit it",
          successtip: "✓ WORK DEFENSE!\n'I did submit it!' = emphatic! Stronger defense than simple past! 💼💪"
        },
        {
          type: "blank",
          q: "Add emphasis: 'They arrived on time' → 'They ___ arrive on time' (defending them)",
          a: "did",
          accept: { ignoreCase: true },
          successtip: "✓ DEFENDING OTHERS!\n'They did arrive on time' = defending them! Confirming they weren't late! ⏰✅"
        },
        {
          type: "mc",
          q: "Friend: 'You forgot my birthday!' You: '___! I sent you a card!'",
          options: [
            "I didn't forget (or: I did remember!)",
            "I forget",
            "I will remember"
          ],
          a: "I didn't forget (or: I did remember!)",
          successtip: "✓ BIRTHDAY DEFENSE!\n'I didn't forget!' or 'I did remember!' = both emphatic! Contradicting accusation! 🎂💪"
        },
        {
          type: "order",
          q: "Traduce con énfasis: Él SÍ terminó el proyecto a tiempo",
          a: "He did finish the project on time",
          successtip: "✓ CONFIRMING COMPLETION!\n'He did finish' = emphatic confirmation! Yes, he really completed it! ✅"
        },
        {
          type: "mc",
          q: "Parent: 'Did you do your homework?' Child: 'Yes! ___!' (emphatic yes)",
          options: [
            "I did do it (double emphasis!)",
            "I do it",
            "I will do it"
          ],
          a: "I did do it (double emphasis!)",
          successtip: "✓ DOUBLE DID!\n'I did do it' = perfectly fine! Sounds strange but grammatically correct! Super emphatic! 💪💪"
        },
        {
          type: "blank",
          q: "Correct the error: 'She did went to the store' → 'She ___ ___ to the store'",
          a: "did go",
          accept: { ignoreCase: true, alts: ["did / go"] },
          successtip: "✓ ERROR FIXED!\n'Did go' NOT 'did went'! Always base verb after 'did'! ✅"
        },
        {
          type: "mc",
          q: "Sibling: 'You ate my cookie!' You: '___! It wasn't me!'",
          options: [
            "I didn't eat it (or: I didn't!)",
            "I don't eat it",
            "I won't eat it"
          ],
          a: "I didn't eat it (or: I didn't!)",
          successtip: "✓ DENYING!\n'I didn't eat it!' = emphatic denial! 'Didn't' already emphatic in negative! 🍪❌"
        },
        {
          type: "order",
          q: "Traduce: Nosotros SÍ llegamos temprano - esperamos una hora",
          a: "We did arrive early - we waited an hour",
          successtip: "✓ PROVING POINT!\n'We did arrive early' + proof = strong argument! Emphasis + evidence! ⏰✅"
        },
        {
          type: "mc",
          q: "Teacher: 'Nobody studied!' Student: 'I ___!' (emphatic individual response)",
          options: [
            "did (I did study!)",
            "do",
            "studied"
          ],
          a: "did (I did study!)",
          successtip: "✓ STANDING OUT!\n'I did!' = short emphatic answer! Full: 'I did study!' Separating yourself from group! 📚"
        },
        {
          type: "mc",
          q: "Summary: 'Did' for emphasis is most common when:",
          options: [
            "Defending yourself, contradicting, or strongly confirming",
            "Making normal statements",
            "Asking questions"
          ],
          a: "Defending yourself, contradicting, or strongly confirming",
          successtip: "✓ EMPHASIS CONTEXTS!\nUse 'did' emphasis when: defending, contradicting accusations, or strongly confirming! 💪✅"
        }
      ]
    },

    // ========================================================================
    // SET 9: IRREGULAR PAST VERBS - CHILDHOOD 📚
    // ========================================================================
    {
      id: "m7-u10-s9",
      title: "Set 9: Irregular Past - Childhood Actions 📚",
      context: `**Childhood Stories**

Sharing memories: "When I was a child..."`,
      
      questions: [
        {
          type: "match",
          q: "Match infinitive verbs with their irregular past forms:",
          pairs: [
            { left: "Feed", right: "Fed (alimentar)" },
            { left: "Bite", right: "Bit (morder)" },
            { left: "Draw", right: "Drew (dibujar)" },
            { left: "Hide", right: "Hid (esconder)" }
          ],
          successtip: "✓ IRREGULAR PASTS!\nThese verbs don't add -ed! Must memorize! Common in childhood stories! 📚"
        },
        {
          type: "mc",
          q: "You: 'My mom ___ me vegetables every day.' (alimentar - past)",
          options: [
            "fed",
            "feeded",
            "feed"
          ],
          a: "fed",
          successtip: "✓ FED!\nFeed → Fed → Fed! Irregular! 'Mom fed me' = common childhood memory! 🥦"
        },
        {
          type: "blank",
          q: "You: 'My brother ___ me when we fought!' (morder - past of 'bite')",
          a: "bit",
          accept: { ignoreCase: true },
          successtip: "✓ BIT!\nBite → Bit → Bitten! Sibling fights = classic childhood! 😬"
        },
        {
          type: "mc",
          q: "You: 'I ___ pictures of my family all the time.' (dibujar - past)",
          options: [
            "drew",
            "drawed",
            "drawn"
          ],
          a: "drew",
          successtip: "✓ DREW!\nDraw → Drew → Drawn! Drawing = common kid activity! 🎨"
        },
        {
          type: "order",
          q: "Traduce: Nos escondimos en el armario durante el escondite (hide - past)",
          a: "We hid in the closet during hide-and-seek",
          successtip: "✓ HID!\nHide → Hid → Hidden! Hide-and-seek = classic kids' game! 🙈"
        },
        {
          type: "mc",
          q: "You: 'My toy boat ___ in the pool.' (hundir - past of sink)",
          options: [
            "sank",
            "sinked",
            "sunk"
          ],
          a: "sank",
          successtip: "✓ SANK!\nSink → Sank → Sunk! Toy boats sinking = childhood pool memories! 🚢💦"
        },
        {
          type: "blank",
          q: "You: 'My older sister ___ us to the park every Saturday.' (guiar - past of 'lead')",
          a: "led",
          accept: { ignoreCase: true },
          successtip: "✓ LED!\nLead → Led → Led! Older siblings often led younger ones! 👧➡️👦"
        },
        {
          type: "mc",
          q: "Complete: 'When we played pirates, our toy ship ___ in the bathtub.'",
          options: [
            "sank (hundir)",
            "sinked",
            "sink"
          ],
          a: "sank (hundir)",
          successtip: "✓ BATHTUB PIRATES!\nSink → Sank! Playing pirates = making ships sink! Classic bath time! 🛁🏴‍☠️"
        },
        {
          type: "order",
          q: "Traduce: Mi hermano me guió a la escuela el primer día (lead - past)",
          a: "My brother led me to school on the first day",
          successtip: "✓ LED TO SCHOOL!\nLead → Led! Older siblings helping younger ones! Sweet memory! 👦➡️🏫"
        },
        {
          type: "mc",
          q: "Which is CORRECT?",
          options: [
            "I fed the dog, hid my toys, and drew pictures (all irregular pasts!)",
            "I feeded the dog, hided my toys, and drawed pictures",
            "I feed the dog, hide my toys, and draw pictures"
          ],
          a: "I fed the dog, hid my toys, and drew pictures (all irregular pasts!)",
          successtip: "✓ ALL IRREGULAR!\nFed, hid, drew = all correct irregular past forms! Common childhood activities! 🐕🎨"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE MASTERY 🌟
    // ========================================================================
    {
      id: "m7-u10-s10",
      title: "Set 10: Family Memories - Complete Mastery 🌟",
      context: `**FINAL CHALLENGE**

You're sharing complete family stories using ALL Unit 10 concepts!`,
      
      questions: [
        {
          type: "mc",
          q: "You: 'My ___ is my father's sister.' (extended family)",
          options: [
            "aunt",
            "cousin",
            "sister"
          ],
          a: "aunt",
          successtip: "✓ AUNT RELATIONSHIP!\nYour father's or mother's sister = aunt! Extended family! 👩"
        },
        {
          type: "blank",
          q: "You: 'My brother's wife brought their baby - my ___.' (your sibling's child)",
          a: "nephew",
          accept: { ignoreCase: true, alts: ["niece", "nephew or niece"] },
          successtip: "✓ NEPHEW/NIECE!\nYour sibling's children = nephew (boy) or niece (girl)! 👶"
        },
        {
          type: "mc",
          q: "You: 'The baby is 2 years old - he's a ___.' (life stage)",
          options: [
            "toddler",
            "teenager",
            "adult"
          ],
          a: "toddler",
          successtip: "✓ TODDLER!\n2 years old = toddler stage (1-3 years)! Learning to walk! 👶🚶"
        },
        {
          type: "order",
          q: "Traduce usando UK: El bebé necesita un pañal limpio",
          a: "The baby needs a clean nappy",
          successtip: "✓ UK BABY VOCAB!\n'Nappy' = UK term for diaper! Baby vocabulary mastered! 🍼🇬🇧"
        },
        {
          type: "mc",
          q: "Grandpa: 'I ___ teach you to ride a bike - don't you remember?' (emphasis!)",
          options: [
            "did (emphasis - insisting)",
            "do",
            "done"
          ],
          a: "did (emphasis - insisting)",
          successtip: "✓ DID EMPHASIS!\n'I did teach you' = emphatic! Grandpa insisting it really happened! 💪🚲"
        },
        {
          type: "blank",
          q: "You: 'When I was little, I ___ pictures on all the walls!' (draw - irregular past)",
          a: "drew",
          accept: { ignoreCase: true },
          successtip: "✓ DREW!\nDraw → Drew! Childhood memory! Drawing on walls = classic kid behavior! 🎨"
        },
        {
          type: "mc",
          q: "You: 'My ___ has a daughter from his first marriage.' (mother's new husband)",
          options: [
            "stepfather",
            "father",
            "uncle"
          ],
          a: "stepfather",
          successtip: "✓ BLENDED FAMILY!\nStepfather = mother's new husband! His daughter = your stepsister! 🏠"
        },
        {
          type: "order",
          q: "Add emphasis: 'She finished her homework' → 'She ___ ___ her homework!'",
          a: "did finish",
          successtip: "✓ DID + BASE VERB!\n'Did finish' = emphatic form! Defending or confirming! 💪"
        },
        {
          type: "mc",
          q: "You: 'We ___ under the table during hide-and-seek!' (hide - irregular past)",
          options: [
            "hid",
            "hided",
            "hide"
          ],
          a: "hid",
          successtip: "✓ HID!\nHide → Hid → Hidden! Classic hiding spot - under the table! 🙈"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which sentence shows COMPLETE Unit 10 knowledge?",
          options: [
            "'My aunt did bring her toddler nephew. We drew pictures and hid toys. He needs his pacifier and crib!'",
            "'My sister bring her teenage cousin. We drawed pictures. He need his dummy!'",
            "'My mother have baby. We play. He sleep!'"
          ],
          a: "'My aunt did bring her toddler nephew. We drew pictures and hid toys. He needs his pacifier and crib!'",
          successtip: "🎉 UNIT 10 COMPLETELY MASTERED! 🎉\n\n✅ FAMILY VOCABULARY\n• Basic: mother, father, sister, brother ✅\n• Extended: aunt, uncle, cousin, nephew, niece ✅\n• In-laws: sister-in-law, brother-in-law, etc. ✅\n• Blended: stepfather, stepsister, half-brother ✅\n\n✅ LIFE STAGES\n• Baby (0-1 year) ✅\n• Toddler (1-3 years) ✅\n• Child (3-12 years) ✅\n• Teenager (13-19 years) ✅\n• Adult (18+ years) ✅\n• Elderly (65+ years) ✅\n\n✅ BABY VOCABULARY\n• US: Pacifier, Diaper, Crib, Stroller ✅\n• UK: Dummy, Nappy, Cot, Pram ✅\n• Universal: Bottle, High chair ✅\n\n✅ 'DID' FOR EMPHASIS\n• Structure: Subject + DID + base verb ✅\n• Use: Defend, contradict, confirm ✅\n• 'I did call you!' = emphasis ✅\n• Stress 'DID' when speaking! ✅\n\n✅ IRREGULAR PAST VERBS\n• Feed → Fed ✅\n• Bite → Bit ✅\n• Sink → Sank ✅\n• Draw → Drew ✅\n• Hide → Hid ✅\n• Lead → Led ✅\n\n✅ INNOVACIÓN: SET 4 STORY\n• Interactive family story ✅\n• Passages intercalados ✅\n• Immersive learning experience ✅\n\n✅ CORRECT IN FINAL SENTENCE:\n• 'Aunt' (extended family) ✅\n• 'Did bring' (emphasis) ✅\n• 'Toddler nephew' (life stage + family) ✅\n• 'Drew' (irregular past) ✅\n• 'Hid' (irregular past) ✅\n• 'Pacifier and crib' (US baby vocab) ✅\n\n🌟 YOU CAN NOW:\n• Describe all family relationships ✅\n• Use UK and US baby vocabulary ✅\n• Talk about life stages ✅\n• Use 'did' for emphasis ✅\n• Use irregular childhood verbs ✅\n• Tell family stories naturally ✅\n\nB1-B2 Family & Early Years = MASTERED! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - MY EARLY YEARS (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u10-reading",
    title: "Reading Practice - My Early Years",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Family Introduction (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**My Early Years**

**Part 1: My Family**

I grew up in a big family. My parents had three children - me, my older sister, and younger brother. We lived with my grandmother. Every summer, my aunt and uncle visited with my cousins. My nephew was born when I was a teenager. Family reunions were amazing!`
      },
      {
        type: "mc",
        q: "How many siblings does the narrator have?",
        options: [
          "Two (older sister + younger brother)",
          "Three",
          "One"
        ],
        a: "Two (older sister + younger brother)",
        successtip: "✓ SIBLING COUNT!\nThree children total = narrator + 2 siblings! Older sister + younger brother! 👧👦"
      },
      {
        type: "blank",
        q: "The narrator's parent's sister = narrator's ___.",
        a: "aunt",
        accept: { ignoreCase: true },
        successtip: "✓ AUNT RELATIONSHIP!\nParent's sister = aunt! Extended family member! 👩"
      },

      // ======================================================================
      // BLOQUE 2: Toddler Years (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 2: When I Was a Toddler**

According to my mom, I was an active toddler! She fed me vegetables, but I only wanted cookies. I bit my brother once during a fight. I drew on the walls with crayons. My parents still have those drawings! I loved hiding under beds during hide-and-seek.`
      },
      {
        type: "mc",
        q: "What life stage is described?",
        options: [
          "Toddler (1-3 years old)",
          "Teenager",
          "Baby"
        ],
        a: "Toddler (1-3 years old)",
        successtip: "✓ TODDLER YEARS!\nActive, drawing, hiding = toddler behaviors! 1-3 years old! 👶"
      },
      {
        type: "blank",
        q: "Past of 'feed': Mom ___ me vegetables.",
        a: "fed",
        accept: { ignoreCase: true },
        successtip: "✓ IRREGULAR VERB!\nFeed → Fed! Mom fed the toddler! 🥦"
      },
      {
        type: "mc",
        q: "Past of 'draw' and 'hide': 'I ___ on walls and ___ under beds.'",
        options: [
          "drew, hid",
          "drawed, hided",
          "drawn, hidden"
        ],
        a: "drew, hid",
        successtip: "✓ BOTH IRREGULAR!\nDraw → Drew! Hide → Hid! Toddler activities! 🎨🙈"
      },

      // ======================================================================
      // BLOQUE 3: Favorite Childhood Memories (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 3: Childhood Memories**

My sister did teach me to read before school started - I remember clearly! My brother and I built a treehouse. Our toy boats sank in the pond constantly. Dad led us on hiking trips every weekend. We fed ducks at the park. Those were happy times!`
      },
      {
        type: "mc",
        q: "Why 'My sister did teach me' instead of 'taught me'?",
        options: [
          "For emphasis - the narrator remembers it clearly and wants to emphasize it happened",
          "It's the wrong grammar",
          "They mean completely different things"
        ],
        a: "For emphasis - the narrator remembers it clearly and wants to emphasize it happened",
        successtip: "✓ DID FOR EMPHASIS!\n'Did teach' = emphatic! Confirming/emphasizing it really happened! 💪"
      },
      {
        type: "blank",
        q: "Past of 'sink': Our boats ___ in the pond.",
        a: "sank",
        accept: { ignoreCase: true },
        successtip: "✓ SANK!\nSink → Sank → Sunk! Toy boats sinking = classic childhood! 🚢"
      },
      {
        type: "mc",
        q: "Past of 'lead': 'Dad ___ us on trips.'",
        options: [
          "led",
          "leaded",
          "lead"
        ],
        a: "led",
        successtip: "✓ LED!\nLead → Led → Led! Dad guided/led the family! 👨➡️"
      },

      // ======================================================================
      // BLOQUE 4: Teenage Years (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 4: Becoming a Teenager**

At thirteen, I became a teenager. Life changed dramatically! My sister had a baby - I became an aunt! Holding my newborn niece in the hospital was magical. She needed constant diaper changes and used a pacifier. Watching her grow from baby to toddler was incredible!`
      },
      {
        type: "mc",
        q: "At what age did the narrator become a teenager?",
        options: [
          "13 years old (when 'teen' appears in age)",
          "10 years old",
          "15 years old"
        ],
        a: "13 years old (when 'teen' appears in age)",
        successtip: "✓ TEENAGE BEGINS!\nThirTEEN = first 'teen' age! Teenager = 13-19 years! 🧒"
      },
      {
        type: "blank",
        q: "The narrator's sister's baby = narrator's ___.",
        a: "niece",
        accept: { ignoreCase: true, alts: ["nephew", "niece or nephew"] },
        successtip: "✓ NIECE!\nSister's daughter = niece! Sister's son = nephew! Sibling's child! 👶"
      },
      {
        type: "mc",
        q: "Life stages mentioned in order:",
        options: [
          "Baby → Toddler → Teenager (correct progression)",
          "Teenager → Baby → Toddler",
          "Toddler → Teenager → Baby"
        ],
        a: "Baby → Toddler → Teenager (correct progression)",
        successtip: "✓ LIFE PROGRESSION!\nBaby (newborn) → Toddler (growing) → Teenager (narrator)! 👶➡️🧒"
      },

      // ======================================================================
      // BLOQUE 5: Family Traditions + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 5: Family Traditions**

Our family did maintain traditions - we really valued them! Every holiday, my aunt, uncle, and cousins gathered. My stepbrother joined us after Dad remarried. We're now a blended family, but love remains strong. From toddler to adult, family defined my early years. These memories shaped me!`
      },
      {
        type: "mc",
        q: "Why 'did maintain' instead of 'maintained'?",
        options: [
          "For emphasis - stressing they really kept traditions",
          "It's incorrect grammar",
          "It's future tense"
        ],
        a: "For emphasis - stressing they really kept traditions",
        successtip: "✓ EMPHASIS!\n'Did maintain' = emphatic! Really valued and kept traditions! 💪"
      },
      {
        type: "blank",
        q: "What family type is mentioned? '___' family (modern mixed family)",
        a: "blended",
        accept: { ignoreCase: true },
        successtip: "✓ BLENDED FAMILY!\nBlended = families mixed through remarriage! Stepbrother joined! 🏠"
      },
      {
        type: "mc",
        q: "Throughout the entire reading, what was covered?",
        options: [
          "All Unit 10 topics: family vocabulary, life stages, irregular verbs, 'did' emphasis, baby items",
          "Only family vocabulary",
          "Only grammar"
        ],
        a: "All Unit 10 topics: family vocabulary, life stages, irregular verbs, 'did' emphasis, baby items",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ FAMILY RELATIONSHIPS\n• Parents, siblings (sister, brother) ✅\n• Extended family (aunt, uncle, cousins) ✅\n• Niece/nephew ✅\n• Blended family (stepbrother) ✅\n\n✅ LIFE STAGES IN ORDER\n• Toddler → Child → Teenager → Adult ✅\n• Baby mentioned (newborn niece) ✅\n• Natural progression through life ✅\n\n✅ IRREGULAR PAST VERBS\n• Fed (feed) ✅\n• Bit (bite) ✅\n• Drew (draw) ✅\n• Hid (hide) ✅\n• Sank (sink) ✅\n• Led (lead) ✅\n• All in childhood contexts! ✅\n\n✅ 'DID' FOR EMPHASIS\n• 'Did teach me' (confirming) ✅\n• 'Did maintain traditions' (emphasizing) ✅\n• Used to stress importance! ✅\n\n✅ BABY"
     }
    ]
  },
  reviewSet: {
    id: "m7-u10-review",
    title: "⭐ Unit 10 Review: Family & Early Years",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Your aunt and uncle's daughter is your:",
        options: [
          "Cousin (same generation)",
          "Niece",
          "Sister"
        ],
        a: "Cousin (same generation)",
        successtip: "✓ COUSIN RELATIONSHIP!\nAunt/uncle's children = your cousins! Same generation! Note: 'cousin' same for male/female! 👫"
      },
      
      {
        type: "blank",
        q: "Your brother's wife is your ___-in-law.",
        a: "sister",
        accept: { ignoreCase: true },
        successtip: "✓ IN-LAW TERM!\nYour sibling's spouse = brother/sister-in-law! Your spouse's sibling also = in-law! 💍"
      },
      
      {
        type: "mc",
        q: "A child who is 2 years old and learning to walk is a:",
        options: [
          "Toddler (1-3 years old)",
          "Teenager",
          "Baby"
        ],
        a: "Toddler (1-3 years old)",
        successtip: "✓ TODDLER STAGE!\nToddler = 1-3 years old, learning to walk! Between baby and child! 👶🚶"
      },
      
      {
        type: "order",
        q: "US: 'diaper' = UK: '___' (pañal)",
        a: "nappy",
        successtip: "✓ UK vs US!\nUS: diaper. UK: nappy. Same baby item! 🍼🇬🇧🇺🇸"
      },
      
      {
        type: "mc",
        q: "What's the difference? 'I called her' vs 'I DID call her':",
        options: [
          "'Did call' adds EMPHASIS - insisting it really happened",
          "They mean opposite things",
          "No difference"
        ],
        a: "'Did call' adds EMPHASIS - insisting it really happened",
        successtip: "✓ DID FOR EMPHASIS!\n'Did + base verb' = emphatic past! For defending, contradicting, confirming! 💪"
      },
      
      {
        type: "blank",
        q: "Past tense of 'feed': My mom ___ me vegetables every day. (irregular)",
        a: "fed",
        accept: { ignoreCase: true },
        successtip: "✓ IRREGULAR VERB!\nFeed → Fed → Fed! Common childhood memory! 🥦"
      },
      
      {
        type: "mc",
        q: "Your mother's new husband (not your biological father) is your:",
        options: [
          "Stepfather",
          "Father-in-law",
          "Uncle"
        ],
        a: "Stepfather",
        successtip: "✓ STEPFATHER!\n'Step-' = through parent's remarriage! Blended families! 👨🏠"
      },
      
      {
        type: "order",
        q: "Add emphasis: 'She finished her homework' → 'She ___ ___ her homework!'",
        a: "did finish",
        successtip: "✓ EMPHATIC PAST!\n'Did finish' = emphasis! Structure: did + base verb! 💪✅"
      },
      
      {
        type: "mc",
        q: "Past tense of 'draw' and 'hide': 'I ___ pictures and ___ under the bed.'",
        options: [
          "drew, hid",
          "drawed, hided",
          "drawn, hidden"
        ],
        a: "drew, hid",
        successtip: "✓ BOTH IRREGULAR!\nDraw → Drew! Hide → Hid! Childhood activities! 🎨🙈"
      },
      
      {
        type: "mc",
        q: "FINAL: Complete Unit 10 mastery includes:",
        options: [
          "Family vocabulary (extended, in-laws, blended) + Life stages + Baby items (UK/US) + 'Did' emphasis + Irregular past verbs",
          "Only family vocabulary",
          "Only grammar"
        ],
        a: "Family vocabulary (extended, in-laws, blended) + Life stages + Baby items (UK/US) + 'Did' emphasis + Irregular past verbs",
        successtip: "🎉 UNIT 10 COMPLETELY MASTERED! 🎉\n\n✅ FAMILY VOCABULARY\n• Basic: mother, father, sister, brother ✅\n• Extended: aunt, uncle, cousin ✅\n• Nephew, niece (sibling's children) ✅\n• In-laws: sister-in-law, brother-in-law ✅\n• Blended: stepfather, stepmother ✅\n• Stepsister, stepbrother ✅\n• Half-sister, half-brother ✅\n\n✅ LIFE STAGES MASTERED\n• Baby (0-1 year) ✅\n• Toddler (1-3 years) ✅\n• Child (3-12 years) ✅\n• Teenager (13-19 years) ✅\n• Adult (18+ years) ✅\n• Elderly (65+ years) ✅\n\n✅ BABY VOCABULARY (UK vs US)\n• Pacifier (US) = Dummy (UK) ✅\n• Diaper (US) = Nappy (UK) ✅\n• Crib (US) = Cot (UK) ✅\n• Stroller (US) = Pram (UK) ✅\n• Bottle, High chair (universal) ✅\n\n✅ 'DID' FOR EMPHASIS\n• Structure: did + BASE verb ✅\n• Use: defend, contradict, confirm ✅\n• 'I did call her!' (emphasis) ✅\n• 'She did finish!' (confirming) ✅\n• Stress 'DID' when speaking! ✅\n\n✅ IRREGULAR PAST VERBS\n• Feed → Fed (alimentar) ✅\n• Bite → Bit (morder) ✅\n• Sink → Sank (hundir) ✅\n• Draw → Drew (dibujar) ✅\n• Hide → Hid (esconder) ✅\n• Lead → Led (liderar/guiar) ✅\n• All childhood contexts! ✅\n\n✅ INNOVACIÓN - SET 4\n• Interactive family story ✅\n• Passages intercalados ✅\n• Garcia Family Reunion ✅\n• Immersive narrative learning ✅\n• 4 passages + questions ✅\n\n✅ READING COMPLETE\n• My Early Years narrative ✅\n• 5 passages covering all topics ✅\n• Natural vocabulary integration ✅\n\n🌟 YOU CAN NOW:\n• Describe all family relationships ✅\n• Talk about life stages accurately ✅\n• Use UK and US baby vocabulary ✅\n• Emphasize with 'did' naturally ✅\n• Use irregular childhood verbs ✅\n• Tell family stories fluently ✅\n\nB1-B2 Family & Early Years = MASTERED! 💪👨‍👩‍👧‍👦\n\n¡Excelente trabajo! Unit 10 complete! 🚀✨"
      }
    ]
  }
};
